import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trash2 } from "lucide-react";

export default function TeamAdmin() {
  const { user } = useAuth();
  const [syn, setSyn] = useState(null);
  const [allSyns, setAllSyns] = useState([]);
  const [chosen, setChosen] = useState(user?.syndicate_id || "");

  useEffect(() => {
    api.get("/syndicates").then((r) => setAllSyns(r.data));
    if (user.role !== "admin" && user.syndicate_id) setChosen(user.syndicate_id);
  }, []);

  useEffect(() => { if (chosen) api.get(`/syndicates/${chosen}`).then((r)=>setSyn(r.data)); }, [chosen]);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
      <div className="small-label text-rtrl-red">Team Console</div>
      <h1 className="font-display uppercase text-5xl md:text-6xl text-white mt-2">Owner Panel</h1>

      {(user.role === "admin" || user.role === "moderator") && (
        <div className="mt-6 rtrl-card carbon-bg p-4 flex items-center gap-3">
          <div className="small-label">Manage syndicate:</div>
          <select className="rtrl-input max-w-sm" value={chosen} onChange={(e)=>setChosen(e.target.value)} data-testid="admin-syn-select">
            <option value="">—</option>
            {allSyns.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      )}

      {!chosen && user.role !== "admin" && user.role !== "moderator" && <div className="mt-8 text-white/50">Your account is not linked to a syndicate yet.</div>}

      {syn && (() => {
        const logoUrl = syn.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${syn.logo_url}` : null;
        return (
          <div className="mt-8 space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-6 bg-surface border border-surface-line p-5 rounded hud-chamfer">
              <div className="flex items-center gap-4">
                {logoUrl ? (
                  <img src={logoUrl} alt={syn.name} className="w-16 h-16 rounded-full object-contain bg-void/50 border border-surface-line shadow-[0_0_15px_rgba(255,255,255,0.05)]" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-void/50 border-2 border-dashed border-surface-line flex items-center justify-center text-[10px] text-white/20 font-mono-tech select-none shrink-0">NO LOGO</div>
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-6 shrink-0" style={{ background: syn.color }}/>
                    <h2 className="font-display uppercase text-4xl text-white leading-none">{syn.name}</h2>
                  </div>
                  <div className="small-label mt-1">{syn.tag} · Team Console</div>
                </div>
              </div>

              {/* Logo Upload Form */}
              <div className="flex items-center gap-3">
                <label className="rtrl-btn-ghost text-xs cursor-pointer px-4 py-2 hover:border-rtrl-red hover:text-rtrl-red transition-all">
                  Change Logo
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/webp"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      if (file.size > 2 * 1024 * 1024) {
                        toast.error("File exceeds 2MB limit.");
                        return;
                      }
                      
                      const fData = new FormData();
                      fData.append("file", file);
                      try {
                        await api.post(`/syndicates/${syn.id}/logo`, fData, {
                          headers: { "Content-Type": "multipart/form-data" }
                        });
                        toast.success("Logo uploaded and optimized!");
                        // Reload syndicate details
                        api.get(`/syndicates/${syn.id}`).then((r) => setSyn(r.data));
                      } catch (err) {
                        toast.error(formatApiError(err.response?.data?.detail));
                      }
                    }}
                  />
                </label>
              </div>
            </div>

            <Tabs defaultValue="roster" className="mt-8">
              <TabsList className="bg-surface border border-surface-line hud-chamfer-sm" data-testid="team-tabs">
                <TabsTrigger value="roster" data-testid="team-tab-roster">Roster</TabsTrigger>
                <TabsTrigger value="lineups" data-testid="team-tab-lineups">Lineups (Planning)</TabsTrigger>
                <TabsTrigger value="results" data-testid="team-tab-results">Post Results</TabsTrigger>
              </TabsList>
              <TabsContent value="roster"><Roster syn={syn} reload={()=>api.get(`/syndicates/${chosen}`).then((r)=>setSyn(r.data))} /></TabsContent>
              <TabsContent value="lineups"><Lineups syn={syn} /></TabsContent>
              <TabsContent value="results"><PostResults syn={syn} /></TabsContent>
            </Tabs>
          </div>
        );
      })()}
    </div>
  );
}

function Roster({ syn, reload }) {
  const { user } = useAuth();
  const [freeAgents, setFAs] = useState([]);
  const [newRacer, setNewRacer] = useState({ display_name: "", number: "", discipline: "both", platform: "PC" });

  useEffect(() => { api.get("/racers").then((r)=>setFAs(r.data.filter(x=>!x.syndicate_id))); }, [syn.id]);

  const addExisting = async (rid) => {
    const r = freeAgents.find(x=>x.id===rid);
    await api.patch(`/racers/${rid}`, { ...r, syndicate_id: syn.id, status: "active" });
    toast.success("Racer added to roster");
    setFAs((prev)=>prev.filter(x=>x.id!==rid));
    reload();
  };

  const drop = async (rid) => {
    await api.delete(`/racers/${rid}`);
    toast.success("Racer dropped to Free Agency");
    reload();
    api.get("/racers").then((r)=>setFAs(r.data.filter(x=>!x.syndicate_id)));
  };

  const createRacer = async (e) => {
    e.preventDefault();
    try {
      await api.post("/racers", {
        display_name: newRacer.display_name,
        number: newRacer.number ? parseInt(newRacer.number, 10) : null,
        discipline: newRacer.discipline,
        platform: newRacer.platform,
        syndicate_id: syn.id,
        status: "active",
      });
      toast.success("Racer created");
      setNewRacer({ display_name: "", number: "", discipline: "both", platform: "PC" });
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-6 grid md:grid-cols-2 gap-6">
      <div>
        <h3 className="font-display uppercase text-2xl text-white">Current Roster ({syn.roster.length}/17)</h3>
        <div className="mt-3 space-y-2">
          {syn.roster.map((r,i)=>(
            <div key={r.id} className="border border-surface-line px-4 py-3 flex items-center justify-between hud-chamfer-sm" data-testid={`roster-row-${i}`}>
              <div>
                <div className="text-white">{r.display_name} <span className="font-mono-tech text-white/40">#{r.number ?? "-"}</span></div>
                <div className="small-label">
                  {(r.discipline==="pod"||r.discipline==="both") && <span className="disc-chip-pod mr-1">Pod</span>}
                  {(r.discipline==="speeder"||r.discipline==="both") && <span className="disc-chip-speeder">Speeder</span>}
                </div>
              </div>
              <button onClick={()=>drop(r.id)} className="text-danger hover:text-white p-2" data-testid={`drop-${i}`}><Trash2 className="w-4 h-4"/></button>
            </div>
          ))}
        </div>
      </div>

      <div>
        {user?.role === "admin" && (
          <div className="mb-8">
            <h3 className="font-display uppercase text-2xl text-white">Add / Recruit</h3>
            <form onSubmit={createRacer} className="mt-3 rtrl-card carbon-bg p-4 grid grid-cols-2 gap-3">
              <input className="rtrl-input col-span-2" placeholder="Racer name" required value={newRacer.display_name} onChange={(e)=>setNewRacer({...newRacer, display_name:e.target.value})} data-testid="new-racer-name"/>
              <input className="rtrl-input" type="number" placeholder="#" value={newRacer.number} onChange={(e)=>setNewRacer({...newRacer, number:e.target.value})} data-testid="new-racer-number"/>
              <select className="rtrl-input" value={newRacer.discipline} onChange={(e)=>setNewRacer({...newRacer, discipline:e.target.value})} data-testid="new-racer-disc">
                <option value="pod">Pod</option><option value="speeder">Speeder</option><option value="both">Both</option>
              </select>
              <button className="rtrl-btn col-span-2" data-testid="new-racer-submit">Create Racer</button>
            </form>
          </div>
        )}

        <h3 className="font-display uppercase text-2xl text-white">Free Agents</h3>
        <div className="mt-3 space-y-2 max-h-96 overflow-auto pr-2">
          {freeAgents.map((r,i)=>(
            <div key={r.id} className="border border-surface-line px-3 py-2 flex items-center justify-between hud-chamfer-sm" data-testid={`fa-row-${i}`}>
              <div className="text-white">{r.display_name} <span className="font-mono-tech text-white/40 text-sm">#{r.number ?? "-"}</span></div>
              <button className="rtrl-btn-ghost text-xs" onClick={()=>addExisting(r.id)} data-testid={`fa-sign-${i}`}>Sign</button>
            </div>
          ))}
          {freeAgents.length===0 && <div className="small-label">No free agents.</div>}
        </div>
      </div>
    </div>
  );
}

function PostResults({ syn }) {
  const { user } = useAuth();
  const [nights, setNights] = useState([]);
  const [nightId, setNightId] = useState("");
  const [detail, setDetail] = useState(null);
  const [raceId, setRaceId] = useState("");
  const [race, setRace] = useState(null);
  const [eligible, setEligible] = useState([]);
  const [positions, setPositions] = useState({}); // racer_id -> position
  const [times, setTimes] = useState({}); // racer_id -> race_time
  const [tracks, setTracks] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState("");
  const [isLocked, setIsLocked] = useState(false);

  const loadRaceData = () => {
    if (!raceId) return;
    api.get(`/races/${raceId}`).then(async (r)=>{
      setRace(r.data);
      setSelectedTrack(r.data.track_name || "");
      setIsLocked(r.data.is_completed || false);
      const roundDisc = r.data.round.discipline;
      const roundSyndicates = [r.data.round.syndicate_a_id, r.data.round.syndicate_b_id];
      const lockedRacerIds = r.data.locked_racer_ids || [];
      
      // Pull racers from both syndicates matching discipline AND are locked in for this race
      const all = await api.get("/racers");
      let list = all.data.filter(x => 
        roundSyndicates.includes(x.syndicate_id) && 
        (x.discipline===roundDisc || x.discipline==="both") &&
        lockedRacerIds.includes(x.id)
      );
      
      // Filter list to only show our own syndicate racers if we are owner/gm
      if (user.role === "syndicate_owner" || user.role === "gm") {
        list = list.filter(x => x.syndicate_id === syn.id);
      }
      
      setEligible(list);
      const posMap = {};
      const timeMap = {};
      (r.data.results || []).forEach(res=> {
        posMap[res.racer_id] = res.position;
        timeMap[res.racer_id] = res.race_time || "";
      });
      setPositions(posMap);
      setTimes(timeMap);
    });
  };

  useEffect(() => {
    api.get("/race-nights").then((r)=>setNights(r.data));
    api.get("/tracks").then((r)=>setTracks(r.data)).catch(()=>{});
  }, [syn.id]);

  useEffect(() => { if (nightId) api.get(`/race-nights/${nightId}/full`).then((r)=>setDetail(r.data)); }, [nightId]);
  useEffect(() => {
    loadRaceData();
  }, [raceId, user.role, syn.id]);

  const eligibleRounds = useMemo(() => {
    if (!detail) return [];
    return detail.rounds.filter(r => r.syndicate_a_id === syn.id || r.syndicate_b_id === syn.id);
  }, [detail, syn.id]);

  const submit = async () => {
    const entries = Object.entries(positions)
      .filter(([_, p]) => p && parseInt(p,10) > 0)
      .map(([racer_id, position]) => ({
        racer_id,
        position: parseInt(position, 10),
        dnf: false,
        race_time: times[racer_id] || ""
      }));
    // detect duplicates
    const set = new Set(entries.map(e=>e.position));
    if (set.size !== entries.length) return toast.error("Duplicate positions detected");
    try {
      const payload = { entries };
      if (user.role === "admin" || user.role === "moderator") {
        payload.track_name = selectedTrack || "";
      }
      await api.post(`/races/${raceId}/results`, payload);
      toast.success("Results submitted — race has been locked.");
      loadRaceData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleUnlock = async () => {
    try {
      await api.post(`/races/${raceId}/unlock`);
      toast.success("Race unlocked for correction.");
      loadRaceData();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-6 space-y-6">
      <div className="rtrl-card carbon-bg p-4 grid md:grid-cols-3 gap-3">
        <select className="rtrl-input" value={nightId} onChange={(e)=>{setNightId(e.target.value); setRaceId(""); setRace(null);}} data-testid="post-select-night">
          <option value="">— Select Race Night —</option>
          {nights.map(n=><option key={n.id} value={n.id}>{n.label}</option>)}
        </select>
        {detail && (
          <select className="rtrl-input md:col-span-2" value={raceId} onChange={(e)=>setRaceId(e.target.value)} data-testid="post-select-race">
            <option value="">— Select Race —</option>
            {eligibleRounds.map(r=>(r.races||[]).map(rc=>(
              <option key={rc.id} value={rc.id}>Round {r.round_number} · Race {rc.race_number} · {r.discipline.toUpperCase()} · {rc.track_name||"—"}</option>
            )))}
          </select>
        )}
      </div>

      {race && (
        <div className="rtrl-card carbon-bg p-6">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4 pb-4 border-b border-surface-line/40">
            <div className="flex items-center gap-3">
              {race.round.discipline==="pod" ? <span className="disc-chip-pod">Pod</span> : <span className="disc-chip-speeder">Speeder</span>}
              <div className="font-display text-3xl text-white uppercase">Race {race.race_number} · {race.track_name||"Track TBD"}</div>
            </div>
            
            {(user.role === "admin" || user.role === "moderator") && (
              <div className="flex items-center gap-2">
                <span className="small-label shrink-0 text-rtrl-silver font-semibold">Active Track:</span>
                <select
                  disabled={isLocked}
                  className="rtrl-input text-sm w-56 font-mono-tech"
                  value={selectedTrack}
                  onChange={(e) => setSelectedTrack(e.target.value)}
                  data-testid="post-results-track-select"
                >
                  <option value="">— Track TBD —</option>
                  {tracks.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                </select>
              </div>
            )}
          </div>
          <p className="small-label mt-2">Assign finishing positions. Points auto-calculate from the league points table.</p>
          <div className="mt-6 grid md:grid-cols-2 gap-3">
            {eligible.map((r)=>(
              <div key={r.id} className="border border-surface-line px-3 py-2 flex items-center justify-between hud-chamfer-sm gap-4" data-testid={`pos-row-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-white truncate font-medium">{r.display_name}</div>
                  <div className="small-label">#{r.number ?? "-"} · {r.syndicate_id===syn.id ? "Home" : "Away"}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <input
                    disabled={isLocked}
                    type="text"
                    placeholder="MM:SS.mmm"
                    value={times[r.id] || ""}
                    onChange={(e) => setTimes({ ...times, [r.id]: e.target.value })}
                    className="rtrl-input w-28 font-mono-tech text-xs"
                    data-testid={`time-input-${r.id}`}
                  />
                  <input
                    disabled={isLocked}
                    type="number"
                    min={1}
                    placeholder="Pos"
                    value={positions[r.id] || ""}
                    onChange={(e)=>setPositions({ ...positions, [r.id]: e.target.value })}
                    className="rtrl-input w-14 font-mono-tech text-center"
                    data-testid={`pos-input-${r.id}`}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-3 items-center">
            {isLocked ? (
              <div className="flex items-center gap-3">
                <span className="text-emerald-400 font-mono-tech text-sm font-bold bg-emerald-500/10 border border-emerald-500/20 px-3 py-2 rounded flex items-center gap-1.5" data-testid="results-locked-indicator">
                  🔒 Results Locked (Completed)
                </span>
                {user.role === "admin" && (
                  <button
                    onClick={handleUnlock}
                    className="rtrl-btn-ghost text-xs px-4 py-2 hover:border-rtrl-red hover:text-rtrl-red font-mono-tech uppercase"
                    data-testid="results-unlock-btn"
                  >
                    🔓 Unlock Race
                  </button>
                )}
              </div>
            ) : (
              <button className="rtrl-btn" onClick={submit} data-testid="post-submit">Submit Results</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Lineups({ syn }) {
  const [nights, setNights] = useState([]);
  const [nightId, setNightId] = useState("");
  const [detail, setDetail] = useState(null);
  const [raceId, setRaceId] = useState("");
  const [race, setRace] = useState(null);
  const [selectedRacers, setSelectedRacers] = useState([]);

  useEffect(() => { api.get("/race-nights").then((r)=>setNights(r.data)); }, [syn.id]);
  useEffect(() => { if (nightId) api.get(`/race-nights/${nightId}/full`).then((r)=>setDetail(r.data)); }, [nightId]);

  useEffect(() => {
    if (!raceId) { setRace(null); return; }
    api.get(`/races/${raceId}`).then((r) => {
      setRace(r.data);
      const rosterIds = new Set((syn.roster || []).map(x => x.id));
      const ourLocked = (r.data.locked_racer_ids || []).filter(rid => rosterIds.has(rid));
      setSelectedRacers(ourLocked);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raceId, syn.id]);

  const eligibleRounds = useMemo(() => {
    if (!detail) return [];
    return detail.rounds.filter(r => r.syndicate_a_id === syn.id || r.syndicate_b_id === syn.id);
  }, [detail, syn.id]);

  const eligibleRacers = useMemo(() => {
    if (!race) return [];
    const disc = race.round.discipline;
    return syn.roster.filter(r => r.discipline === disc || r.discipline === "both");
  }, [race, syn.roster]);

  const toggleRacer = (rid) => {
    setSelectedRacers((prev) =>
      prev.includes(rid) ? prev.filter((x) => x !== rid) : [...prev, rid]
    );
  };

  const saveLineup = async () => {
    const requiredCount = race.round.discipline === "pod" ? 4 : 6;
    if (selectedRacers.length !== requiredCount) {
      toast.error(`You must select exactly ${requiredCount} drivers for ${race.round.discipline.toUpperCase()} racing.`);
      return;
    }
    try {
      await api.put(`/races/${raceId}/locked-racers`, { racer_ids: selectedRacers });
      toast.success("Lineup successfully saved and locked in!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="mt-6 space-y-6">
      <div className="rtrl-card carbon-bg p-4 grid md:grid-cols-3 gap-3">
        <select className="rtrl-input" value={nightId} onChange={(e)=>{setNightId(e.target.value); setRaceId(""); setRace(null);}} data-testid="lineup-select-night">
          <option value="">— Select Race Night —</option>
          {nights.map(n=><option key={n.id} value={n.id}>{n.label}</option>)}
        </select>
        {detail && (
          <select className="rtrl-input md:col-span-2" value={raceId} onChange={(e)=>setRaceId(e.target.value)} data-testid="lineup-select-race">
            <option value="">— Select Race —</option>
            {eligibleRounds.map(r=>(r.races||[]).map(rc=>(
              <option key={rc.id} value={rc.id}>Round {r.round_number} · Race {rc.race_number} · {r.discipline.toUpperCase()} · {rc.track_name||"—"}</option>
            )))}
          </select>
        )}
      </div>

      {race && (
        <div className="rtrl-card carbon-bg p-6">
          <div className="flex items-center gap-3">
            {race.round.discipline==="pod" ? <span className="disc-chip-pod">Pod</span> : <span className="disc-chip-speeder">Speeder</span>}
            <div className="font-display text-3xl text-white uppercase">Race {race.race_number} · {race.track_name||"Track TBD"} Lineup</div>
          </div>
          <p className="small-label mt-2">Planning Mode: Lock in racers who will participate in this race tomorrow night.</p>
          
          <div className="mt-6 space-y-2 max-w-xl">
            {eligibleRacers.map((r) => {
              const isChecked = selectedRacers.includes(r.id);
              return (
                <div key={r.id} onClick={() => toggleRacer(r.id)} className={`cursor-pointer border px-4 py-3 flex items-center justify-between hud-chamfer-sm transition-all ${isChecked ? "border-rtrl-red bg-rtrl-red/10" : "border-surface-line hover:border-surface-hi"}`} data-testid={`lineup-row-${r.id}`}>
                  <div>
                    <div className="text-white font-medium">{r.display_name}</div>
                    <div className="small-label">#{r.number ?? "-"} · {r.platform || "PC"}</div>
                  </div>
                  <div className={`w-5 h-5 rounded border flex items-center justify-center ${isChecked ? "bg-rtrl-red border-rtrl-red text-white" : "border-white/20"}`}>
                    {isChecked && "✓"}
                  </div>
                </div>
              );
            })}
            {eligibleRacers.length === 0 && <div className="text-white/40">No eligible racers in roster for this discipline.</div>}
          </div>
          <button className="rtrl-btn mt-6" onClick={saveLineup} data-testid="lineup-save-btn">Lock In Lineup</button>
        </div>
      )}
    </div>
  );
}
